import { softMergeSearchMapPins } from 'components/Search/SearchResultsMap/utils'
import { queryKeyBounds, queryKeyPages } from 'constants/url'

export type ExperienceMapPinUpdateMode = 'bounds' | 'replace'

function serialiseExperienceMapFilterKey(search: string): string {
  const params = new URLSearchParams(search)
  params.delete(queryKeyBounds)
  params.delete(queryKeyPages)
  const keys = [...new Set([...params.keys()])].sort()
  return keys.map(key => `${key}=${params.getAll(key).slice().sort().join(',')}`).join('&')
}

/**
 * Soft-merge pins only when the viewport bounds change. Any other search change
 * (campaign, collection, dates, destination, rating, …) replaces pins.
 * Pagination (`pages`) does not change the mode.
 */
export function getExperienceMapPinUpdateMode(
  previousSearch: string | undefined,
  nextSearch: string,
): ExperienceMapPinUpdateMode | undefined {
  if (previousSearch == null || previousSearch === nextSearch) return undefined

  const previousBounds = new URLSearchParams(previousSearch).get(queryKeyBounds) ?? ''
  const nextBounds = new URLSearchParams(nextSearch).get(queryKeyBounds) ?? ''
  const boundsChanged = previousBounds !== nextBounds
  const filtersUnchanged = serialiseExperienceMapFilterKey(previousSearch) ===
    serialiseExperienceMapFilterKey(nextSearch)

  if (boundsChanged && filtersUnchanged) return 'bounds'
  if (!filtersUnchanged) return 'replace'
  return undefined
}

/**
 * Hotel map (OSS-2250) keeps previous pins while a bounds search is in flight,
 * then merges new pins in front. Experience map streaming was hard-replacing
 * on the first hydrated batch, which made pins blink on every drag.
 *
 * Returns `undefined` when the caller should keep the current pin list.
 */
export function nextExperienceMapPinOffers<T extends { id: string }>({
  fetching,
  experiences,
  previousOffers,
  mode,
}: {
  fetching: boolean
  experiences: Array<T>
  previousOffers: Array<T>
  mode: ExperienceMapPinUpdateMode
}): Array<T> | undefined {
  if (fetching) return undefined

  if (experiences.length === 0) {
    return mode === 'bounds' ? undefined : []
  }

  if (mode === 'bounds' && previousOffers.length > 0) {
    return softMergeSearchMapPins(previousOffers, experiences, (offer: T) => offer.id)
  }

  return experiences
}
